"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "./ui";

/** Local preview only. The existing clone action is the sole upload path. */
export function VoiceRecorder({ language, disabled, onFile, onBusy }: {
  language: string; disabled: boolean; onFile: (file: File) => void; onBusy: (busy: boolean) => void;
}) {
  const [status, setStatus] = useState<"idle" | "requesting" | "recording">("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const [sample, setSample] = useState<{ file: File; url: string } | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const generation = useRef(0);
  const previewUrl = useRef<string | null>(null);
  const busyCallback = useRef(onBusy); busyCallback.current = onBusy;

  function release() {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
  }
  useEffect(() => () => {
    generation.current++;
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
    release();
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    busyCallback.current(false);
  }, []);

  function cancel() {
    generation.current++;
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
    recorder.current = null;
    release(); setStatus("idle"); setSeconds(0); onBusy(false);
  }

  async function start() {
    if (disabled || status !== "idle") return;
    const attempt = ++generation.current;
    setError(""); setStatus("requesting"); onBusy(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("Recording needs a supported browser over HTTPS. You can still upload an audio file.");
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (attempt !== generation.current) { media.getTracks().forEach(track => track.stop()); return; }
      stream.current = media;
      const mime = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"].find(type => MediaRecorder.isTypeSupported(type));
      if (!mime) throw new Error("This browser cannot record a supported audio format. Please upload an audio file instead.");
      const instance = new MediaRecorder(media, { mimeType: mime });
      recorder.current = instance;
      const chunks: Blob[] = [];
      let started = 0;
      instance.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      instance.onerror = () => { if (attempt === generation.current) { cancel(); setError("Recording failed. Please try again or upload a file."); } };
      instance.onstop = () => {
        if (attempt !== generation.current) return;
        const elapsed = (performance.now() - started) / 1000;
        release(); setStatus("idle"); onBusy(false);
        if (elapsed < 10) { setError("Please record at least 10 seconds of clear speech, then try again."); return; }
        const type = (instance.mimeType || mime).split(";")[0];
        const blob = new Blob(chunks, { type });
        if (!blob.size || blob.size > 20 * 1024 * 1024) { setError("Recording is empty or too large. Please record again."); return; }
        const extension = type === "audio/mp4" ? "m4a" : type === "audio/ogg" ? "ogg" : "webm";
        const file = new File([blob], `voice-recording-${Date.now()}.${extension}`, { type });
        if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
        const url = URL.createObjectURL(file); previewUrl.current = url;
        setSample({ file, url }); setSeconds(Math.floor(elapsed));
        onFile(file);
      };
      instance.start(); started = performance.now(); setSeconds(0); setStatus("recording");
      timer.current = setInterval(() => {
        const elapsed = (performance.now() - started) / 1000;
        setSeconds(Math.floor(elapsed));
        if (elapsed >= 30 && instance.state === "recording") instance.stop();
      }, 100);
    } catch (reason) {
      if (attempt !== generation.current) return;
      release(); setStatus("idle"); onBusy(false);
      setError(reason instanceof DOMException && reason.name === "NotAllowedError"
        ? "Microphone access was denied. Allow the microphone in your browser settings, or upload a file."
        : reason instanceof Error ? reason.message : "Microphone unavailable. Please upload a file.");
    }
  }

  return <div className="space-y-3 rounded-xl border border-line p-3">
    <p className="text-sm font-medium">Record your voice by reading aloud</p>
    <p className="text-xs leading-5 text-muted">1. Read the passage below. 2. Press Start recording and speak for at least 10 seconds. 3. Stop, listen, and save your voice sample after confirming permission to use this voice.</p>
    <p className="text-xs leading-5 text-muted">Read naturally for at least 10 seconds in a quiet room. Stops automatically at 30 seconds. No music or other speakers. You can also read your own passage in the selected language.</p>
    <blockquote className="rounded-lg bg-white/5 p-3 text-sm leading-6">{language === "zh"
      ? "你好，这是我的声音样本。今天的阳光很好，我想用自然、清晰的声音，分享生活中的故事。希望每一句话，都能让听到的人感受到温暖和快乐。我现在正在录制自己的声音。"
      : "Hello, this is a sample of my natural voice. I enjoy sharing stories and discovering something new every day. Today I am speaking clearly and calmly, just as I would when talking to a friend."}</blockquote>
    {language !== "en" && language !== "zh" && <p className="text-xs text-muted">Translate this example or read your own passage in the language selected above.</p>}
    <div className="flex flex-wrap items-center gap-2">
      {status === "idle" ? <Button variant="secondary" size="sm" disabled={disabled} onClick={() => void start()}>{sample ? "Record again" : "● Start recording"}</Button>
        : <><span role="status" className="text-sm">{status === "requesting" ? "Waiting for microphone permission…" : `● Recording ${seconds}s / 30s`}</span>{status === "recording" && <Button size="sm" disabled={seconds < 10} onClick={() => { if (recorder.current?.state === "recording") recorder.current.stop(); }}>Stop{seconds < 10 ? ` (${10 - seconds}s more)` : ""}</Button>}<Button size="sm" variant="secondary" onClick={cancel}>Cancel</Button></>}
    </div>
    {error && <p role="alert" className="text-xs text-amber-200">{error}</p>}
    {sample && status === "idle" && <><p role="status" className="text-xs text-muted">Recording ready ({seconds}s). Automatically added as your voice sample. Listen below or record again.</p><audio controls src={sample.url} className="h-10 w-full" /><Button variant="secondary" size="sm" disabled={disabled} onClick={() => onFile(sample.file)}>Use this recording</Button><p className="text-xs text-muted">Local preview only. Nothing is uploaded until you save or clone this sample.</p></>}
  </div>;
}
